import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFirebaseConnect } from 'react-redux-firebase'
import { useHistory, useLocation } from 'react-router-dom'

import {
    LimitNotification,
    ProLimitationDialog,
    BasicLimitationDialog,
    ProPlanStartedDialog,
    UpgradePlanDialog,
    TrialPlanStartedDialog,
    TrialEndDialog,
    TrialWeekToEndDialog,
    UpdatePlanDialog
} from 'UI/Components'
import { RoleDowngradeBanner } from './RoleDowngradeBanner'

import {
    useServiceUsage,
    useStorage,
    planTypes,
    useAuth,
    useRoles,
    useWindowView,
    useQueryParams,
    useNotification
} from 'hooks'
import { showPlanDialog, setNewAuth, showPlanNotifications, closePlanDialogs } from 'ducks'
import { filterURLParams } from 'modules'

const usageDialogBreakpoint = [75, 100, 120]

// this file is file-controller for the component
// which are connected to the local storage/data base
// but the main goal is to display notifications in a special order
export const ServiceUsageController = () => {
    const auth = useSelector(state => state.firebase.auth)
    const {
        dialogs: { updatePlanDialog, upgradePlanDialog },
        notifications: { trialPlanStarted, paidPlanLimitation, freePlanLimitation, paidPlanStarted }
    } = useSelector(state => state.serviceUsage)

    const { roles, newAuth, orgId, uid } = useAuth()
    const currentOrgUser = useSelector(state => state.firestore.data.orgUsers?.[uid])
    const { isViewer, isAdmin } = useRoles()
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()

    const {
        usedPercent,
        activePlanId,
        disableTrialDialog,
        isLoading,
        isTrialPlan,
        planName,
        isFreePlan,
        trialDuration,
        isReversedTrial
    } = useServiceUsage()

    const [planId, setPlanId] = useState(activePlanId)
    const queryParams = useQueryParams()

    const showPlans = queryParams.get('showPlans')

    const { isDesktopView } = useWindowView()
    const { showSuccessNotification } = useNotification()

    const [nextUsageBreakpoint, setNextUsageBreakpoint] = useStorage(
        'gd-notification-dialog',
        0,
        'localStorage'
    )

    useFirebaseConnect(`stripe/${orgId}/showDialog/${uid}`)

    const trialDialogsData = useSelector(
        state => state.firebase.data.stripe?.[orgId]?.showDialog?.[uid]
    )

    const { trialEnded, trialWillEndAt } = trialDialogsData || {}

    useEffect(() => {
        // show dialog that paid/trial plan started when plan id actually changed id db
        if (isLoading || !activePlanId) return

        setPlanId(activePlanId)

        if (planId && planId !== activePlanId && activePlanId !== planTypes.individual) {
            dispatch(
                showPlanNotifications({
                    trialPlanStarted: isTrialPlan,
                    paidPlanStarted: !isTrialPlan
                })
            )
        }
    }, [activePlanId, planId, isLoading, dispatch, isTrialPlan])

    // open Dialog once or after login if usage more than 75%/100%/120% of his plan
    // and store data in local storage
    useEffect(() => {
        if (isLoading) return

        if (usedPercent < usageDialogBreakpoint[0]) {
            dispatch(
                showPlanNotifications({ freePlanLimitation: false, paidPlanLimitation: false })
            )
        }

        const exceededBreakpoints = usageDialogBreakpoint.filter(n => n > usedPercent)
        const newNextBreakpoint = exceededBreakpoints.length
            ? Math.min(...exceededBreakpoints)
            : Math.max(...usageDialogBreakpoint)
        const showAfterLogin = newAuth && usedPercent > usageDialogBreakpoint[0]

        setNextUsageBreakpoint(newNextBreakpoint)

        if (!showAfterLogin && newNextBreakpoint <= nextUsageBreakpoint) {
            return
        }

        dispatch(
            showPlanNotifications({
                freePlanLimitation: isFreePlan,
                paidPlanLimitation: !isFreePlan
            })
        )
    }, [
        dispatch,
        newAuth,
        nextUsageBreakpoint,
        usedPercent,
        setNextUsageBreakpoint,
        isLoading,
        activePlanId,
        isFreePlan
    ])

    const paidPlanStartedParam = useQueryParams().get('redirect_status')
    const pathname = history.location.pathname
    const historyReplace = history.replace

    useEffect(() => {
        // notify about operation status after page reload when user changes plan

        if (paidPlanStartedParam !== 'succeeded') {
            return
        }

        showSuccessNotification('We’ve received your request. Your plan will be changed shortly.')
        historyReplace(pathname)
    }, [paidPlanStartedParam, pathname, historyReplace, showSuccessNotification])

    useEffect(() => {
        if (!showPlans) return

        dispatch(showPlanDialog('upgradePlanDialog'))
        historyReplace(filterURLParams(location, ['showPlans']))
    }, [dispatch, historyReplace, location, showPlans])

    if (auth.isAnonymous || roles?.n || activePlanId === planTypes.enterprise) {
        return null
    }

    // to not show nothing if user role is viewer
    if (isViewer) return null

    const showDowngradeBanner =
        currentOrgUser?.viewerOnlyAfter && currentOrgUser.viewerOnlyAfter.toMillis() > Date.now()

    return (
        <>
            {/* will be removed in the future, these components haven't mobile view */}
            {isDesktopView && (
                <>
                    {upgradePlanDialog && (
                        <UpgradePlanDialog onClose={() => dispatch(closePlanDialogs())} />
                    )}
                    {updatePlanDialog && (
                        <UpdatePlanDialog
                            onClose={() => dispatch(closePlanDialogs())}
                            onChangePlan={() => dispatch(showPlanDialog('upgradePlanDialog'))}
                        />
                    )}

                    {trialPlanStarted && !isReversedTrial && (
                        <TrialPlanStartedDialog
                            trialDays={trialDuration}
                            onClose={() =>
                                dispatch(showPlanNotifications({ trialPlanStarted: false }))
                            }
                        />
                    )}
                    {paidPlanStarted && (
                        <ProPlanStartedDialog
                            planName={planName}
                            isFreePlan={isFreePlan}
                            onClose={() =>
                                dispatch(showPlanNotifications({ paidPlanStarted: false }))
                            }
                        />
                    )}

                    {paidPlanLimitation && (
                        <ProLimitationDialog
                            onClose={() => {
                                dispatch(showPlanNotifications({ paidPlanLimitation: false }))
                                dispatch(setNewAuth(false))
                            }}
                            onPrimaryClick={() => {
                                if (isAdmin) {
                                    dispatch(showPlanDialog('upgradePlanDialog'))
                                }
                            }}
                        />
                    )}
                    {freePlanLimitation && (
                        <BasicLimitationDialog
                            onClose={() => {
                                dispatch(showPlanNotifications({ freePlanLimitation: false }))
                                dispatch(setNewAuth(false))
                            }}
                        />
                    )}

                    {trialEnded && !isTrialPlan && isFreePlan && (
                        <TrialEndDialog onClose={() => disableTrialDialog('trialEnded')} />
                    )}
                    {trialWillEndAt && isTrialPlan && (
                        <TrialWeekToEndDialog
                            onClose={() => disableTrialDialog('trialWillEndAt')}
                            date={trialWillEndAt}
                        />
                    )}
                </>
            )}
            {showDowngradeBanner ? (
                <RoleDowngradeBanner date={currentOrgUser.viewerOnlyAfter?.toMillis()} />
            ) : (
                <LimitNotification />
            )}
        </>
    )
}
